/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { benefitsMessages } from 'src/appShell/homePage/homePageBenefitsMessages';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';

type BenefitSectionInfo = {
    title: ReactIntl.FormattedMessage.MessageDescriptor;
    description: ReactIntl.FormattedMessage.MessageDescriptor;
    imageSrc: string;
};

const { styleComponent, styleDiv, getClassName } = getStylist('HomePageBenefits');

const BenefitsRootContainer = styleDiv<{ isLargeScreen: boolean }>('HomePageBenefitsRootContainer', {}, ({ customProps }, theme) => ({
    padding: customProps.isLargeScreen ? theme.containerPaddingLarge : theme.containerPaddingSmall
}));

const BenefitsTitle = styleComponent<ITextProps>(Text)('HomePageDemoTitle', theme => ({
    fontSize: theme.titleFontSize,
    textAlign: 'center'
}));

const BenefitSection = styleComponent<IStackProps>(Stack)('HomePageBenefitSection', theme => ({
    padding: theme.benefitSectionPadding,
    borderBottom: theme.benefitSectionBorderBottom
}));

const BenefitDescription = styleComponent<LuisTextProps>(LuisText)('BenefitDescription', theme => ({
    lineHeight: theme.descriptionLineSpacing
}));
const benefitImageClassName = getClassName('BenefitImage', { maxWidth: '95%' });

const HalfWidthStackItem = styleComponent<IStackProps>(Stack)<{ isLargeScreen: boolean }>('HalfWidthStackItem', {}, ({ customProps }) => ({
    width: customProps.isLargeScreen ? '50%' : '100%'
}));

/**
 * Renders the benefits section in the home page.
 */
export const HomePageBenefits = () => {
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();

    return (
        <BenefitsRootContainer role="region" customProps={{ isLargeScreen: isXLargeOrGreater }}>
            <Stack horizontalAlign="center">
                <BenefitsTitle variant="xxLarge" as="h3" block>
                    {localize(benefitsMessages.mainTitle)}
                </BenefitsTitle>

                {benefitsInfo.map((b, index) => (
                    <BenefitSection
                        key={getId()}
                        tokens={{ childrenGap: 30 }}
                        horizontal={isXLargeOrGreater}
                        verticalAlign="center"
                        reversed={isXLargeOrGreater ? index % 2 === 0 : false}>
                        <HalfWidthStackItem horizontalAlign="center" customProps={{ isLargeScreen: isXLargeOrGreater }}>
                            <img
                                className={benefitImageClassName}
                                alt={localize(b.title)}
                                src={getAssetsUrl(`assets/images/homePage/${b.imageSrc}`)}
                            />
                        </HalfWidthStackItem>

                        <HalfWidthStackItem customProps={{ isLargeScreen: isXLargeOrGreater }} tokens={{ childrenGap: 50 }}>
                            <LuisText variant="xxLarge" as="h4" fontWeight={400} block>
                                {localize(b.title)}
                            </LuisText>

                            <BenefitDescription variant="large">{localize(b.description)}</BenefitDescription>
                        </HalfWidthStackItem>
                    </BenefitSection>
                ))}
            </Stack>
        </BenefitsRootContainer>
    );
};

const benefitsInfo: BenefitSectionInfo[] = [
    {
        title: benefitsMessages.addLanguageTitle,
        description: benefitsMessages.addLanguageDescription,
        imageSrc: 'intelligenceFeature.png'
    },
    {
        title: benefitsMessages.buildSolutionTitle,
        description: benefitsMessages.buildSolutionDescription,
        imageSrc: 'prebuiltFeature.png'
    },
    {
        title: benefitsMessages.alwaysLearningTitle,
        description: benefitsMessages.alwaysLearningDescription,
        imageSrc: 'targetFeature.png'
    },
    {
        title: benefitsMessages.enterpriseReadyTitle,
        description: benefitsMessages.enterpriseReadyDescription,
        imageSrc: 'monitorFeature.png'
    }
];
